import "react-day-picker/style.css";

import { faChevronDown, faClose } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { cn } from "@gymflow/helpers";
import { PopoverClose } from "@radix-ui/react-popover";
import { cva, VariantProps } from "class-variance-authority";
import { omit } from "lodash";
import { DateTime } from "luxon";
import { useEffect, useMemo, useState } from "react";
import { DateRange, DayPicker, DayPickerProps } from "react-day-picker";

import { useClubSettings } from "../../../providers";
import { CalendarWithNumberIcon } from "../../atoms";
import { Button } from "../../atoms/Button";
import { buttonVariants } from "../../atoms/buttonVariants";
import { Popover, PopoverContent, PopoverTrigger } from "../../atoms/Popover";

const baseClasses =
  "block h-11 w-full flex-1 rounded-lg py-2.5 px-3.5 text-gray-900 placeholder:text-gray-400  sm:text-sm sm:leading-6 dark:bg-gray-950 dark:placeholder:text-gray-500 dark:!text-gray-50 border-0 bg-gray-0";
const defaultColors = "border border-gray-200 dark:!border-gray-800";

const TextInputClassNames = cn(baseClasses, defaultColors);
const datePickerVariants = cva("", {
  variants: {
    inputType: {
      dropdown: cn(
        buttonVariants({ size: "small", intent: "default" }),
        "justify-between bg-gray-0 text-gray-500 border-gray-200 h-10 !rounded-[.625rem] shadow-xs",
        "dark:bg-gray-800 dark:hover:!bg-gray-800 dark:border-gray-700",
      ),
      field: cn(
        TextInputClassNames,
        "flex justify-between bg-[#fff] text-gray-700",
      ),
    },
  },
  defaultVariants: {
    inputType: "dropdown",
  },
});

export type DatePickerProps<T> = DayPickerProps & {
  showQuickNavigation?: boolean;
  label?: string;
  isClearable?: T;
  /***  This field is called `isDisabled` because `disabled` is already taken by react-day-picker */
  isDisabled?: boolean;
  inputClassnames?: string;
  labelClassName?: string;
  manualApply?: boolean;
} & VariantProps<typeof datePickerVariants> &
  (
    | {
        mode: "single";
        handleDateSave?: (
          date: T extends true ? Date | undefined : Date,
        ) => void;
      }
    | {
        mode: "multiple";
        handleDateSave?: (
          date: T extends true ? Date[] | undefined : Date[],
        ) => void;
      }
    | {
        mode: "range";
        handleDateSave?: (
          date: T extends true ? DateRange | undefined : DateRange,
        ) => void;
      }
    | { mode: undefined; handleDateSave?: (date: undefined) => void }
    | undefined
  );

/*** All dates are in local time  */
export const DatePicker = <T,>(props: DatePickerProps<T>) => {
  const { club } = useClubSettings();
  const [month, setMonth] = useState(
    DateTime.now().setZone(club.timezone).toJSDate(),
  );
  const [isOpenPicker, setIsOpenPicker] = useState(false);

  const selectedProp = (props as any).selected;
  const [currentSelected, setCurrentSelected] = useState<any>(selectedProp);

  useEffect(() => {
    if (selectedProp) {
      switch (props.mode) {
        case "single":
          setMonth(selectedProp);
          break;
        case "range":
          if (selectedProp?.start) {
            setMonth(selectedProp.start);
          }
          break;
        case "multiple":
          if (selectedProp?.[0]) {
            setMonth(selectedProp[0]);
          }
          break;
      }
    }
  }, [selectedProp, props.mode]);

  const isAutoApply = props.mode === "single" && !props.manualApply;

  const dayPickerProps = useMemo(() => {
    let result = omit(props, [
      "showQuickNavigation",
      "isClearable",
      "label",
      "handleDateSave",
    ]) as DayPickerProps;
    if (props.handleDateSave) {
      (result as any).selected = currentSelected;
      (result as any).onSelect = (item: any) => {
        setCurrentSelected(item) as any;
        if (isAutoApply) {
          (props.handleDateSave as any)?.(item);
          setIsOpenPicker(false);
        }
      };
    }
    return result;
  }, [props, currentSelected, isAutoApply]);

  return (
    <Popover open={isOpenPicker} onOpenChange={(open) => setIsOpenPicker(open)}>
      <PopoverTrigger
        disabled={props.isDisabled}
        className={cn(
          datePickerVariants({ inputType: props.inputType }),
          props.inputClassnames,
          {
            "bg-gray-100 ring-transparent active:ring-transparent hover:!bg-gray-100 cursor-not-allowed":
              props.isDisabled,
          },
        )}
      >
        <div
          className={cn(
            "font-semibold text-gray-400 line-clamp-1 text-base",
            props.labelClassName,
          )}
        >
          {props.label ?? "Select Date..."}
        </div>
        <div className="flex flex-row items-center gap-x-2">
          {props.isClearable && (dayPickerProps as any).selected && (
            <FontAwesomeIcon
              onClick={(e) => {
                props.handleDateSave?.(undefined as any);
                setCurrentSelected(undefined as any);
                e.stopPropagation();
              }}
              className="size-3"
              icon={faClose}
            />
          )}
          {!props.isDisabled &&
            (props.inputType === "dropdown" || !props.inputType) && (
              <FontAwesomeIcon
                className="size-3 text-gray-500"
                icon={faChevronDown}
              />
            )}
          {props.inputType === "field" && (
            <CalendarWithNumberIcon pathClassName="stroke-gray-500" />
          )}
        </div>
      </PopoverTrigger>
      <PopoverContent>
        {props.showQuickNavigation && (
          <>
            <div className="flex flex-col p-4">
              {[
                {
                  label: "Today",
                  onClick: () => setMonth(DateTime.now().toJSDate()),
                },
                {
                  label: "Yesterday",
                  onClick: () =>
                    setMonth(DateTime.now().minus({ days: 1 }).toJSDate()),
                },
                {
                  label: "This week",
                  onClick: () =>
                    setMonth(
                      DateTime.now()
                        .setZone(club.timezone)
                        .startOf("week")
                        .toJSDate(),
                    ),
                },
                {
                  label: "Last week",
                  onClick: () =>
                    setMonth(
                      DateTime.now()
                        .setZone(club.timezone)
                        .minus({ weeks: 1 })
                        .startOf("week")
                        .toJSDate(),
                    ),
                },
                {
                  label: "This month",
                  onClick: () =>
                    setMonth(
                      DateTime.now()
                        .setZone(club.timezone)
                        .startOf("month")
                        .toJSDate(),
                    ),
                },
                {
                  label: "Last month",
                  onClick: () =>
                    setMonth(
                      DateTime.now()
                        .setZone(club.timezone)
                        .minus({ months: 1 })
                        .startOf("month")
                        .toJSDate(),
                    ),
                },
                {
                  label: "This year",
                  onClick: () =>
                    setMonth(
                      DateTime.now()
                        .setZone(club.timezone)
                        .startOf("year")
                        .toJSDate(),
                    ),
                },
                {
                  label: "Last year",
                  onClick: () =>
                    setMonth(
                      DateTime.now()
                        .setZone(club.timezone)
                        .minus({ years: 1 })
                        .startOf("year")
                        .toJSDate(),
                    ),
                },
              ].map((e, i) => {
                return (
                  <div
                    key={i}
                    className="dark:text-gray-0 flex w-full min-w-fit cursor-pointer whitespace-nowrap rounded p-2 font-medium text-gray-950 hover:bg-gray-50 dark:hover:bg-gray-800"
                    onClick={() => {
                      e.onClick();
                    }}
                  >
                    {e.label}
                  </div>
                );
              })}
            </div>
            <div className="flex border-r border-gray-200 dark:border-gray-800">
              &nbsp;
            </div>
          </>
        )}
        <div className="flex flex-col">
          <div className="dark:text-gray-0 flex p-4">
            <DayPicker
              className="max-w-[calc(100vw-2rem)] overflow-x-auto"
              month={month}
              onMonthChange={(e) => setMonth(e)}
              {...dayPickerProps}
            />
          </div>
          {props.handleDateSave && !isAutoApply && (
            <>
              <div className="flex w-full border-t border-gray-200 dark:border-gray-800"></div>
              <div className="flex w-full flex-row items-center justify-end gap-2 p-4">
                <PopoverClose asChild>
                  <Button
                    onClick={() => {
                      setCurrentSelected((props as any).selected);
                    }}
                  >
                    Cancel
                  </Button>
                </PopoverClose>
                <PopoverClose asChild>
                  <Button
                    onClick={() => {
                      (props.handleDateSave as any)?.(currentSelected);
                    }}
                    intent="secondary"
                  >
                    Apply
                  </Button>
                </PopoverClose>
              </div>
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
